/* eslint-disable prefer-destructuring */
import { ThemeProvider } from '@digital-retail/design-system';
import { Favicons, Provider } from '@digital-retail/falabella-ui-cross-components';
import {
  getLatLongFromAddressLine,
  getSeoPageTitle,
  isSodimacRetail,
  isSodimacStandalone,
  isTottusStandalone,
} from '@digital-retail/store-manager';
import getConfig from 'next/config';
import Head from 'next/head';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { constants } from '../config/constants';
import { storeMap } from '../config/storeMap';
import { renderColorsAsCssVariables } from '../config/styles/colors';
import { LayoutContainer } from '../containers/Layout/LayoutContainer';
import { loadDigitalData } from '../external_dependencies/digitalData';
import { loadExternalDependencies } from '../external_dependencies/index';
import { ApplicationContextProvider, withApplicationContext } from '../utils/ApplicationContext';
import { CookieStorage } from '../utils/CookieStorage';
import '../utils/CoreJsImports';
import { getCustomCssPropertiesStyle } from '../utils/customCssHelper';
import { getDeviceType, getPlatformType, isInternetExplorer, isSearchBot, isWindow } from '../utils/deviceChecker';
import { fetchApplicationData } from '../utils/FetchData';
import { getInterlinkingContent } from '../utils/interlinking/getInterlinkingContent';
import { isInterlinkingEnabled } from '../utils/interlinking/helpers/toggles';
import { Logger, attachErrorLogger } from '../utils/Logger';
import { MyListsContextProvider } from '../utils/MyListsContext';
import { redirectToUrl } from '../utils/RouteHandler';
import { getTheme } from '../utils/theme';
import { isValidData, parseAllZones } from '../utils/tottus.helper';
import { getCtxStoreValue, getPageName, getStore } from '../utils/urlHelper';
import { UserContextProvider } from '../utils/UserContext';
import { WithUrl } from '../utils/WithUrl';

const LayoutWithAppContext = withApplicationContext(LayoutContainer);

const nextConfig = getConfig();
const publicRuntimeConfig = (nextConfig && nextConfig.publicRuntimeConfig) || {};
const serverRuntimeConfig = (nextConfig && nextConfig.serverRuntimeConfig) || {};

const isComponentRenderer = (Component) => {
  return Component.name === 'ComponentRenderer' || Component.name === 'ComponentStylesRenderer';
};
// comment to prevent CC CSS caching
const App = ({ Component, pageProps, appCtx: initialAppCtx, customCssPropertiesStyle }) => {
  const router = useRouter();

  attachErrorLogger();
  const skipExternalScripts = router.query?.skipExternalScripts === 'true';
  useEffect(() => {
    if (!skipExternalScripts) {
      loadExternalDependencies();
    }
    Logger.init(initialAppCtx);
    CookieStorage.setHostOnlyCookie({
      name: 'isPoliticalIdExists',
      value: initialAppCtx?.siteConfig?.toggles?.isPoliticalIdExists ?? false,
      regionCode: initialAppCtx.regionCode,
    });
    const zonesList = (initialAppCtx?.zones ?? '').split(',');
    const zones = JSON.stringify(zonesList);
    CookieStorage.setCookie({
      name: 'priceGroupId',
      value: initialAppCtx?.priceGroupId ?? '',
      regionCode: initialAppCtx.regionCode,
      store: initialAppCtx.store,
    });
    CookieStorage.setCookie({
      name: 'politicalId',
      value: initialAppCtx?.politicalId ?? '',
      regionCode: initialAppCtx.regionCode,
      store: initialAppCtx.store,
    });
    CookieStorage.setCookie({
      name: 'zones',
      value: zones,
      regionCode: initialAppCtx.regionCode,
      store: initialAppCtx.store,
    });
    if (isTottusStandalone(initialAppCtx.store)) {
      const expressZones = (initialAppCtx?.expressZones ?? '').split(',');
      const standardZones = (initialAppCtx?.standardZones ?? '').split(',');
      CookieStorage.setCookie({
        name: 'zonesList',
        value: btoa(JSON.stringify({ expressZones, standardZones })),
        regionCode: initialAppCtx.regionCode,
        store: initialAppCtx.store,
      });
    }
    let zoneID;
    try {
      zoneID = zonesList.find((z) => {
        return (initialAppCtx?.zonesInfo?.availableZones ?? {})[z];
      });
    } catch (error) {
      zoneID = zonesList.slice(-1)[0];
    }
    if (!zoneID) {
      zoneID = zonesList.slice(-1)[0];
    }
    const selectedZone = (initialAppCtx?.zonesInfo?.zones ?? []).find((z) => z.id === zoneID);
    const zoneName = selectedZone?.name ?? constants.DEFAULT_ZONES;
    CookieStorage.setCookie({
      name: 'zoneData',
      value: { zoneID, zoneName },
      regionCode: initialAppCtx.regionCode,
      store: initialAppCtx.store,
    });
    const comuna = initialAppCtx?.comuna ?? {};
    const isDefaultComuna = initialAppCtx?.isDefaultComuna ?? false;
    if (comuna.default || isDefaultComuna) {
      CookieStorage.setCookie({
        name: 'IS_ZONE_SELECTED',
        value: false,
        regionCode: initialAppCtx.regionCode,
        store: initialAppCtx.store,
      });
      CookieStorage.setCookie({
        name: 'userSelectedZone',
        value: 'defaultZone',
        regionCode: initialAppCtx.regionCode,
        store: initialAppCtx.store,
      });
    }

    const COOKIES_PAIR_TO_REMOVE = initialAppCtx?.siteConfig?.textDictionary?.COOKIES_PAIR_TO_REMOVE ?? '';

    CookieStorage.removeCookiePairs(COOKIES_PAIR_TO_REMOVE, initialAppCtx.regionCode, initialAppCtx.store);
  }, []);

  useEffect(() => {
    loadDigitalData(router?.route);
  }, [router?.route]);

  return (
    <ApplicationContextProvider appCtx={initialAppCtx}>
      <Provider
        tenant={initialAppCtx.regionCode}
        baseUrl={
          initialAppCtx?.header?.entry?.header_configs?.[0]?.links?.catalystbaseurl ?? 'https://www.falabella.com'
        }
        deviceType={initialAppCtx.deviceType}
        enableOldStoreInStoreHeaders={initialAppCtx.disableSISTheme}
        commerceOriginData={{
          name: 'XLP',
          view: getPageName(initialAppCtx.url),
        }}
        isSodimacStandAloneSite={initialAppCtx.isSodimacSA || initialAppCtx.isSodimacRE}
        isTottusStandAloneSite={initialAppCtx.isTottusSA}
      >
        <Head>
          <title>{getSeoPageTitle({ store: initialAppCtx.store || getStore(initialAppCtx?.tenant) })}</title>
          <meta name="viewport" content="initial-scale=1.0, width=device-width, maximum-scale=1,user-scalable=no" />
          <meta name="theme-color" content="#FFFFFF" />
          <meta charSet="iso-8859-1" />
          <meta name="HandheldFriendly" content="True" />
          <meta name="MobileOptimized" content="320" />
          <meta name="format-detection" content="telephone=no" />
          <meta name="apple-mobile-web-app-status-bar-style" content="black" />
          <meta httpEquiv="pragma" content="cache" />
          <meta property="og:type" content="article" />
          <meta
            name="apple-mobile-web-app-title"
            content={getSeoPageTitle({ store: initialAppCtx.store || getStore(initialAppCtx?.tenant) })}
          />
          <meta name="language" content="ES" />
          <meta httpEquiv="X-UA-Compatible" content="IE=edge,chrome=1" />
          <meta name="google-site-verification" content="1VMve7ONbnxKqYZ1xuoXrvmrAEs8rMkCFsIfcNUp_Cs" />
          {Favicons.render({
            tenant: initialAppCtx.regionCode,
            store: initialAppCtx.store || getStore(initialAppCtx?.tenant),
          })}
          <link rel="preconnect" href="https://gateway.foresee.com" />
          <link rel="preconnect" href="https://analytics.foresee.com" />
          <link rel="preconnect" href="https://falabella.demdex.net" crossOrigin />
          <link rel="preconnect" href="https://connect.facebook.net" crossOrigin />
          <link rel="preconnect" href="https://www.facebook.com" crossOrigin />
          <link rel="preconnect" href="https://stats.g.doubleclick.net" crossOrigin />
          <style>{customCssPropertiesStyle}</style>
          <style>{renderColorsAsCssVariables()}</style>
        </Head>
        <UserContextProvider>
          <LayoutWithAppContext>
            <MyListsContextProvider appCtx={initialAppCtx}>
              <ThemeProvider theme={getTheme(initialAppCtx.store)}>
                <Component {...pageProps} />
              </ThemeProvider>
            </MyListsContextProvider>
          </LayoutWithAppContext>
        </UserContextProvider>
      </Provider>
    </ApplicationContextProvider>
  );
};

App.getInitialProps = async ({ Component, router, ctx }) => {
  if (!ctx.query.site) {
    const url = WithUrl(404);
    redirectToUrl(url, ctx.res);
    return {};
  }
  const isClientSide = isWindow();
  const cookie = isClientSide ? document.cookie : ctx.req.headers.cookie;
  const cookies = CookieStorage.parseCookies(cookie);
  const { headers } = ctx.req || {};
  const reqUrl = isClientSide ? window.location.pathname + window.location.search : ctx.req.url;
  const { host } = headers || {};
  const buildId = (headers && headers['x-next-build-id']) || '';

  const appCtx = {
    tenant: (ctx.query && ctx.query.site) || '',
    host,
    isComponentRequest: isComponentRenderer(Component),
    deviceType: !isClientSide ? getDeviceType(ctx.req.headers) : getDeviceType(),
    isInternetExplorer: !isClientSide
      ? isInternetExplorer(ctx.req.headers['user-agent'])
      : isInternetExplorer(navigator.userAgent),
    busy: 0,
    buildId,
    ssrCookies: cookie,
    tracingHeaders: {},
    isSearchBot: !isClientSide && ctx.req.headers['user-agent'] && isSearchBot(ctx.req.headers['user-agent']),
    platformType: !isClientSide ? getPlatformType(ctx.req.headers) : getPlatformType(window.navigator.userAgent),
    url: reqUrl,
    zones: cookies.zones,
  };

  appCtx.regionCode = `${appCtx.tenant.substr(appCtx.tenant.indexOf('-') + 1, 2)}`;
  appCtx.store = (ctx.query && ctx.query.store && getCtxStoreValue(ctx.query.store)) || null;

  const isTottusSA = isTottusStandalone(appCtx.store);
  const isSodimacSA = isSodimacStandalone(appCtx.store);
  const isSodimacRE = isSodimacRetail(appCtx.store);

  appCtx.isTottusSA = isTottusSA;
  appCtx.isSodimacSA = isSodimacSA;
  appCtx.isKiosk = cookies?.KIOSKO_STORE_ID ? cookies.KIOSKO_STORE_ID !== '' : false;
  appCtx.isSodimacRE = isSodimacRE;

  Logger.init(appCtx);
  const getHost = (e, tenant, url, store) => {
    const env = e === 'uat' ? 'beta' : e;
    if (tenant !== 'cl' && !isSodimacRetail(store)) {
      return `https://${env}.falabella.com.${tenant}${url}`;
    }
    if (isSodimacRetail(store)) {
      return `https://${env}.sodimac.com.${tenant}${url}`;
    }
    return `https://${env}.falabella.com${url}`;
  };

  publicRuntimeConfig.API_HOST = getHost(
    publicRuntimeConfig.env,
    appCtx.regionCode,
    publicRuntimeConfig.BROWSE_ENDPOINT,
    appCtx.store
  );

  publicRuntimeConfig.CUSTOMER_LOCATION_HOST = getHost(
    publicRuntimeConfig.env,
    appCtx.regionCode,
    publicRuntimeConfig.CL_ENDPOINT,
    appCtx.store
  );

  publicRuntimeConfig.CONTENT_API_HOST = getHost(
    publicRuntimeConfig.env,
    appCtx.regionCode,
    publicRuntimeConfig.CONTENT_ENDPOINT,
    appCtx.store
  );

  const { env, CLUSTER_API_HOST, CLUSTER_CUSTOMER_LOCATION_HOST, CLUSTER_CONTENT_API_HOST } = publicRuntimeConfig;

  if (env === 'local') {
    publicRuntimeConfig.API_HOST = CLUSTER_API_HOST;
    publicRuntimeConfig.CUSTOMER_LOCATION_HOST = CLUSTER_CUSTOMER_LOCATION_HOST;
    publicRuntimeConfig.CONTENT_API_HOST = CLUSTER_CONTENT_API_HOST;
  }

  if (!isClientSide && ctx.res && !(ctx.res.finished || ctx.res.headersSent)) {
    constants.TRACING_HEADERS.forEach((headerName) => {
      if (headers && headers[headerName]) {
        ctx.res.setHeader(headerName, headers[headerName]);
        appCtx.tracingHeaders[headerName] = headers[headerName];
      }
    });
  }

  let parsedZones = [];
  const isValidZones = () => {
    try {
      parsedZones = JSON.parse(cookies.zones);
      return true;
    } catch {
      return false;
    }
  };

  const regionId = ctx?.query?.region_id?.replace(/_/g, '-');

  if (isValidData(regionId)) {
    appCtx.pid = regionId;
  } else {
    appCtx.pid = isValidData(cookies.politicalId) ? cookies.politicalId : 'default';
  }
  const addressLines = isValidData(cookies.addressLines) ? cookies.addressLines : '';
  const latLong = getLatLongFromAddressLine(addressLines);
  appCtx.latLong = latLong;

  const appDataFetcher = fetchApplicationData(publicRuntimeConfig, serverRuntimeConfig, appCtx, ctx.query);

  let services = [];

  if (!isClientSide) {
    services = ['siteConfig', 'transversalBanner', 'header', 'footer', 'taxonomy', 'sisNavigationMenu', 'comuna'];
    if (appCtx.regionCode !== 'mx') {
      services.push('zonesInfo');
    }
    if (isSodimacRE && appCtx.pid) {
      services.push('virtualZones');
    }
  }

  const servicesData = await appDataFetcher.fetch(services);

  services.forEach((service, i) => {
    appCtx[service] = servicesData[i];
  });

  if (!isClientSide && appCtx.siteConfig && !(appCtx?.siteConfig?.toggles?.isPoliticalIdExists ?? false)) {
    const service = ['zonesInfo'];
    const serviceData = await appDataFetcher.fetch(service);
    const [zonesInfo] = serviceData;
    appCtx.zonesInfo = zonesInfo;
  }

  if (!isClientSide && appCtx.siteConfig) {
    appCtx.siteConfig.textDictionary.ITEM_ADDED_IN_CART =
      appCtx?.siteConfig?.textDictionary?.ITEM_ADDED_IN_CART_MKP ?? 'Lo que llevas en tu Carro';
  }

  const comuna = appCtx.comuna?.data;
  const isUserSelectedZone = cookies.userSelectedZone === 'userselected';
  appCtx.isUserSelectedZone = isUserSelectedZone;

  const parseZones = (zones) => {
    try {
      const parsed = JSON.parse(zones);
      return parsed.join();
    } catch {
      return null;
    }
  };

  if (comuna) {
    const zones = comuna.zones || [];
    appCtx.priceGroupId = comuna.priceGroupId;
    appCtx.politicalId = comuna.politicalId;
    appCtx.zones = isSodimacRE ? appCtx.virtualZones?.data?.join() || parseZones(cookies.zones) : zones.join();
    appCtx.isDefaultComuna = appCtx.comuna?.default ?? false ? true : !isUserSelectedZone;
    if (!appCtx.latLong?.latitude || !appCtx.latLong?.longitude) {
      appCtx.latLong = getLatLongFromAddressLine(`***${comuna.latitude}*${comuna.longitude}`);
    }

    if (isTottusStandalone(appCtx.store)) {
      const expressZones = Array.isArray(comuna.expressZones) ? comuna.expressZones : [];
      const standardZones = Array.isArray(comuna.standardZones) ? comuna.standardZones : [];
      appCtx.expressZones = expressZones.join();
      appCtx.standardZones = standardZones.join();
    }
  } else {
    appCtx.priceGroupId = cookies.priceGroupId;
    appCtx.politicalId = cookies.politicalId;
    appCtx.zones = isValidZones() ? parsedZones.join() : '';
    if (isTottusStandalone(appCtx.store)) {
      const allZones = parseAllZones(isTottusStandalone(appCtx.store), cookies, Logger);
      const { expressZones, standardZones } = allZones;
      appCtx.expressZones = expressZones.join();
      appCtx.standardZones = standardZones.join();
    }
  }

  const themeCookie = cookies.EXP_SIS || cookies.exp_sis;
  appCtx.disableSISTheme = themeCookie === 'old';

  appCtx.isSISLevel2Header = cookies.SISLEVEL2_HEADER_AB === 'true';

  // Hide some favicons in sodimac
  // https://jira.falabella.tech/browse/PDSLIST-826
  appCtx.hideSodimacFavicons = ['sodimac-cl', 'sodimac-pe'].includes(`${storeMap[appCtx.store]}-${appCtx.regionCode}`);

  const promises = [Promise.resolve({}), Promise.resolve({})];

  if (Component.getInitialProps) {
    promises[0] = Component.getInitialProps({ config: publicRuntimeConfig }, ctx, appCtx, router);
  }

  if (!isClientSide && isInterlinkingEnabled(appCtx?.siteConfig?.toggles)) {
    promises[1] = getInterlinkingContent({
      appCtx,
      query: ctx.query,
      req: ctx.req,
      config: publicRuntimeConfig,
    });
  }

  const [pageProps, interlinking] = await Promise.all(promises);

  const customCssPropertiesStyle = getCustomCssPropertiesStyle(appCtx);
  appCtx.interlinking = interlinking;

  return {
    pageProps,
    appCtx,
    customCssPropertiesStyle,
  };
};

App.propTypes = {
  pageProps: PropTypes.object,
  Component: PropTypes.func,
  appCtx: PropTypes.object.isRequired,
  customCssPropertiesStyle: PropTypes.string.isRequired,
};

App.defaultProps = {
  pageProps: {},
  Component: () => {},
};

export default App;
